import { useForm, useWatch } from 'react-hook-form'
import { superstructResolver } from '@hookform/resolvers/superstruct'
import { object, string, optional, nonempty } from 'superstruct'
import {
  ControlledInput,
  ControlledSelect,
  ControlledRadioGroupInput,
  GeocodeInput,
  Button,
  ButtonLink,
  StaticMap,
  Badge,
  Title,
  Card,
} from '@/components'

import { countryList } from '@/lib/regions'
import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'

import RentImage from 'public/images/rent.png'

interface addressShape {
  streetName: string
  streetNumber: string
  city: string
  neighbourhood: string
  region: string
  unitNumber: string
  country: string
  postalZip: string
  listingType: string
}

const formSchema = object({
  streetName: nonempty(string()),
  streetNumber: nonempty(string()),
  city: nonempty(string()),
  neighbourhood: nonempty(string()),
  region: nonempty(string()),
  unitNumber: optional(string()),
  country: nonempty(string()),
  postalZip: nonempty(string()),
  listingType: nonempty(string()),
})

interface AddressStepProps {
  country: string
  lat: any
  lng: any
  handleUpdate: (data: any) => void
  handleBack?: () => void
  canGoBack?: boolean
  data?: any
}

export const ListingAddressStep = ({
  country,
  handleUpdate,
  handleBack,
  canGoBack = true,
  lat,
  lng,
  data,
}: AddressStepProps) => {
  const [selectedValue, setSelectedValue] = useState<any>(null)
  const [showFields, setShowFields] = useState(false)
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: superstructResolver(formSchema),
    defaultValues: {
      country: country === 'USA' ? 'USA' : 'Canada',
      ...data,
    },
  })

  const setFromGeocode = (data) => {
    if (!Object.keys(data).length) {
      setShowFields(false)
      setSelectedValue(null)
      reset()
    } else {
      setShowFields(true)
      setValue('streetNumber', data.streetNumber)
      setValue('streetName', data.streetName)
      setValue('neighbourhood', data.neighbourhood)
      setValue('city', data.city)
      setValue('postalZip', data.postalZip)
      setValue('country', data.country)
      setValue('region', data.region)
      setSelectedValue({ lat: data.latitude, lng: data.longitude })
    }
  }

  useEffect(() => {
    if (data) {
      setShowFields(true)
    }
  }, [])

  const listingType = useWatch({ control, name: 'listingType' })

  const formHandler = (data) => {
    if (showFields) {
      handleUpdate(data)
    }
  }

  return (
    <>
      <header className="pt-10 sm:pt-16 pb-6 max-w-7xl mx-auto px-4">
        <p className=" max-w-4xl  text-2xl dark:text-zinc-200 text-zinc-600 font-bold tracking-tight ">
          We'll ask a series of questions about your experience. We only ask you submit honest and accurate portrayals
          of your living experience. The more honest, the better.
        </p>
        <p className="mt-3 sm:mt-4 mb-3 sm:mb-10 max-w-2xl  text-lg dark:text-zinc-200 text-zinc-600 font-medium tracking-tight leading-relaxed">
          All reviews are anonymous by default.
        </p>
        <Badge large>Step 1</Badge>

        <Title as="h1" size="2xl" className="mt-6">
          Let's start with the address.
        </Title>
      </header>
      <div className="md:flex w-full gap-16 pt-2 pb-6 items-start px-4">
        <form onSubmit={handleSubmit(formHandler)} className="focus:outline-none md:w-2/3">
          <Card>
            <div className="py-4">
              <GeocodeInput callback={setFromGeocode} lat={lat} lng={lng} />
              {showFields && (
                <div className="mt-12">
                  <AnimatePresence>
                    <motion.div
                      key="row1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'spring' }}
                      className="pb-6 sm:pb-12 "
                    >
                      <ControlledRadioGroupInput
                        control={control}
                        name="listingType"
                        label="What kind of building are we talking about here?"
                        rules={{ required: true }}
                        options={[
                          { name: 'house', label: 'House', description: '' },
                          { name: 'condo', label: 'Condo', description: '' },
                          { name: 'room', label: 'Room', description: '' },
                          { name: 'apartment', label: 'Apartment', description: '' },
                        ]}
                      />
                    </motion.div>

                    <motion.div
                      key="row2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'spring' }}
                      className="sm:grid grid-cols-6 space-y-6 sm:space-y-0 sm:gap-6 pt-12 border-t border-zinc-300  dark:border-zinc-700 mb-8 "
                    >
                      <ControlledInput
                        name="unitNumber"
                        control={control}
                        label="Unit/Apartment #"
                        placeholder="Optional"
                        className="col-span-2"
                        disabled={listingType === 'house'}
                      />
                    </motion.div>

                    <motion.div
                      key="row3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'spring' }}
                      className="sm:grid grid-cols-6 space-y-6 sm:space-y-0 sm:gap-6  mb-8 "
                    >
                      <ControlledInput
                        name="streetNumber"
                        control={control}
                        rules={{ required: true }}
                        label="Street Number"
                        className="col-span-2"
                        placeholder="100"
                      />
                      <ControlledInput
                        name="streetName"
                        control={control}
                        rules={{ required: true }}
                        label="Street Name"
                        className="col-span-3"
                        placeholder="Main Street W"
                      />
                    </motion.div>

                    <motion.div
                      key="row4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'spring' }}
                      className="sm:grid grid-cols-6 sm:gap-6 mb-8 space-y-6 sm:space-y-0"
                    >
                      <ControlledInput
                        name="neighbourhood"
                        control={control}
                        label="Neighbourhood"
                        className="col-span-2"
                        placeholder="Centretown"
                      />
                      <ControlledInput
                        name="region"
                        control={control}
                        rules={{ required: true }}
                        label="Region"
                        className="col-span-2"
                        placeholder="Ontario"
                      />
                      <ControlledInput
                        name="postalZip"
                        control={control}
                        rules={{ required: true }}
                        label="Postal Code"
                        className="col-span-2"
                        placeholder="A1B 2C3"
                      />
                    </motion.div>

                    <motion.div
                      key="row5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'spring' }}
                      className="sm:grid grid-cols-6 sm:gap-10 mb-4"
                    >
                      <ControlledSelect
                        name="country"
                        control={control}
                        rules={{ required: true }}
                        label="Country"
                        className="col-span-2"
                        options={Object.entries(countryList).map((country) => ({
                          value: country[1],
                          label: country[1],
                        }))}
                      />
                    </motion.div>

                    <motion.div
                      key="row6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'spring' }}
                      className="w-full  flex justify-end py-6 bg-white/50 dark:bg-zinc-900/70 backdrop-blur-2xl bottom-0 z-40 "
                    >
                      {canGoBack && (
                        <ButtonLink type="button" onClick={handleBack}>
                          Back
                        </ButtonLink>
                      )}
                      {showFields && (
                        <Button type="submit" outline alt className="px-16" forward>
                          Next
                        </Button>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </Card>
        </form>
        <div className="rounded-md overflow-hidden md:w-1/3 hidden md:block">
          {selectedValue ? (
            <StaticMap
              showMarker={!!selectedValue}
              config={{
                height: 500,
                latitude: selectedValue?.lat,
                longitude: selectedValue?.lng,
                zoom: 15,
              }}
            />
          ) : (
            <motion.div className="mx-auto" animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
              <Image
                className="object-cover"
                src={RentImage}
                alt="Renter"
                placeholder="blur" // Optional blur-up while loading
              />
            </motion.div>
          )}
        </div>
      </div>
    </>
  )
}
